import React, {useCallback, useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {Button} from 'primereact/button';
import {ProgressBar} from 'primereact/progressbar';
import {Sidebar} from 'primereact/sidebar';
import {InputText} from 'primereact/inputtext';
import {InputNumber} from 'primereact/inputnumber';
import {Dropdown} from 'primereact/dropdown';
import {MultiSelect} from 'primereact/multiselect';
import {Message} from 'primereact/message';
import {recruitmentApi, recruitmentItems} from '../recruitmentApi';
import {eventColor} from './eventColor';

export const RecruitmentShowComponent = ({t}) => {
  const {id} = useParams();
  const history = useHistory();
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [edit, setEdit] = useState(null);
  const canWrite = (localStorage.getItem('roles') || '').includes('role_recruitment_write');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await recruitmentItems('/recruitment-event', {'search[recruitment][operator]': '=', 'search[recruitment][value]': id, sort: 'position', direction: 'asc'});
      setEvents(await Promise.all(items.map(async event => ({...event, counts: (await recruitmentApi('get', `/recruitment-event/${event.id}/counts`)).data.data}))));
    } catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const open = async event => {
    setError('');
    setEdit(event ? {...event, type: event.type?.id, recruitment_challenges: (event.recruitment_challenges || []).map(c => c.id)} : {name: '', position: events.length, type: null, recruitment_challenges: []});
    try {
      const [typeItems, challengeItems] = await Promise.all([recruitmentItems('/recruitment-event-type'), recruitmentItems('/recruitment-challenge')]);
      setTypes(typeItems); setChallenges(challengeItems);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  };
  const save = async () => {
    setSaving(true); setError('');
    try {
      await recruitmentApi(edit.id ? 'patch' : 'post', `/recruitment-event${edit.id ? `/${edit.id}` : ''}`, {
        name: edit.name, position: edit.position, type: edit.type, recruitment: Number(id), recruitment_challenges: edit.recruitment_challenges,
      });
      setEdit(null); await load();
    } catch (e) { setError(e.response?.data?.detail || e.response?.data?.message || e.message); }
    finally { setSaving(false); }
  };
  return <>
    {loading && <ProgressBar mode="indeterminate" style={{height: 5}}/>}
    {!edit && error && <Message severity="error" text={error}/>}
    {canWrite && <Button className="p-button-success p-mb-3" icon="pi pi-plus" label={t('recruitmentWorkflow.newEvent')} onClick={() => open(null)}/>}
    {!loading && !events.length && <p>{t('recruitmentWorkflow.noEvents')}</p>}
    {events.map(event => <section key={event.id} className="p-card p-p-3 p-mb-4" style={{border: '1px solid var(--surface-border, #dce1e7)', borderLeft: `5px solid ${eventColor(event.type?.color)}`, backgroundColor: 'var(--surface-50, #f8f9fa)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', borderRadius: 8}}>
      <div className="p-d-flex p-ai-center p-jc-between"><h3>{event.name || event.type?.name}</h3>
        {canWrite && <Button className="p-button-warning" icon="pi pi-pencil" aria-label={t('edit')} onClick={() => open(event)}/>}
      </div>
      <p>{event.type?.name}</p>
      {!!event.recruitment_challenges?.length && <p>{event.recruitment_challenges.map(c => c.name).join(' • ')}</p>}
      <div className="p-d-flex p-flex-wrap" style={{gap: 8}}>
        {['active', 'all', 'todo', 'results'].map(scope => <Button key={scope} className="p-button-outlined" label={`${t(`recruitmentWorkflow.${scope}`)} (${event.counts?.[scope] ?? 0})`}
          onClick={() => history.push(`/recruitment/recruitment-event-candidate?recruitmentId=${id}&eventId=${event.id}&eventScope=${scope}`)}/>) }
      </div>
    </section>)}
    <Sidebar visible={!!edit} position="right" style={{width: 'min(780px, 100vw)'}} onHide={() => !saving && setEdit(null)}>
      {edit && <div className="p-fluid p-p-3">
        <h2>{t(edit.id ? 'recruitmentWorkflow.editEvent' : 'recruitmentWorkflow.newEvent')}</h2>
        {error && <Message severity="error" text={error}/>}
        <div className="p-field"><label htmlFor="event-name">{t('fieldName')}</label><InputText id="event-name" value={edit.name} onChange={e => setEdit({...edit, name: e.target.value})}/></div>
        <div className="p-field"><label htmlFor="event-type">{t('recruitmentWorkflow.eventType')}</label><Dropdown inputId="event-type" value={edit.type} options={types} optionLabel="name" optionValue="id" filter onChange={e => setEdit({...edit, type: e.value})}/></div>
        <div className="p-field"><label htmlFor="event-position">{t('recruitmentWorkflow.position')}</label><InputNumber inputId="event-position" value={edit.position} min={0} onValueChange={e => setEdit({...edit, position: e.value})}/></div>
        <div className="p-field"><label htmlFor="event-challenges">{t('recruitmentWorkflow.challenges')}</label><MultiSelect inputId="event-challenges" value={edit.recruitment_challenges} options={challenges} optionLabel="name" optionValue="id" filter onChange={e => setEdit({...edit, recruitment_challenges: e.value})}/></div>
        <Button label={t('save')} icon="pi pi-save" className="p-button-success" loading={saving} disabled={!edit.name.trim() || !edit.type || edit.position == null} onClick={save}/>
      </div>}
    </Sidebar>
  </>;
};
export default withTranslation('common')(RecruitmentShowComponent);
